import { ErrorHandler, Injectable } from '@angular/core';

const CHUNK_RELOAD_KEY = 'avyro.chunk-reload';

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  const nested = (error as { message?: unknown; rejection?: unknown; error?: unknown }) ?? {};
  if (typeof nested.message === 'string') {
    return nested.message;
  }
  if (nested.rejection instanceof Error) {
    return nested.rejection.message;
  }
  if (nested.error instanceof Error) {
    return nested.error.message;
  }
  return String(error ?? '');
}

function isPdfViewerTeardownError(error: unknown): boolean {
  const message = errorMessage(error);
  return (
    /AbortException/i.test(message) ||
    /TextLayer task cancelled/i.test(message) ||
    /Rendering cancelled/i.test(message) ||
    /Worker was destroyed/i.test(message)
  );
}

function isChunkLoadError(error: unknown): boolean {
  const message = errorMessage(error);

  return (
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

@Injectable()
export class LoadChunkErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (isPdfViewerTeardownError(error)) {
      return;
    }

    if (isChunkLoadError(error)) {
      const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
      const now = Date.now();

      // Avoid an infinite reload loop if the chunk is genuinely broken.
      if (!Number.isFinite(lastReload) || now - lastReload > 10_000) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
        console.warn('Stale app chunk detected. Reloading to fetch the latest build.');
        document.location.reload();
        return;
      }
    }

    console.error(error);
  }
}
